import React, { FC, ReactElement, useState } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldErrors,
  UseFormReturn,
  UseFormStateReturn,
} from 'react-hook-form';
import { ScheduledEventForm } from '../models/ScheduledEventDto';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Box,
} from '@mui/material';
import { ScheduledRelayCommand4Config } from '@thingslog/repositories/src/scheduledEvents';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps): JSX.Element {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`relay-tabpanel-${index}`}
      aria-labelledby={`relay-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: '0.75rem' }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number): { id: string; 'aria-controls': string } {
  return {
    id: `relay-tab-${index}`,
    'aria-controls': `relay-tabpanel-${index}`,
  };
}

export const ScheduledRelay4Command: FC<ScheduledRelay4CommandProps> = ({
  form,
}: ScheduledRelay4CommandProps) => {
  const {
    register,
    control,
    formState: { errors: formErrors },
  } = form;

  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);

  const errors = formErrors.event as FieldErrors<ScheduledRelayCommand4Config>;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number): void => {
    setTabValue(newValue);
  };

  const renderRelayForm = (relayIndex: number): JSX.Element => {
    const relayErrors = errors?.scheduledRelayCommandParams?.[relayIndex];

    return (
      <div className="grid grid-cols-1 gap-2 gap-y-4 md:grid-cols-2">
        <div>
          <TextField
            label={t('relay_index')}
            variant="outlined"
            type="number"
            {...register(`event.scheduledRelayCommandParams.${relayIndex}.relayIndex`, {
              valueAsNumber: true,
            })}
            error={!!relayErrors?.relayIndex}
            helperText={relayErrors?.relayIndex?.message ?? ''}
            size="small"
            fullWidth
            inputProps={{ min: 0, max: 3 }}
          />
        </div>
        <FormControl fullWidth size="small" error={!!relayErrors?.requiredState}>
          <InputLabel id={`required-state-label-${relayIndex}`}>
            {t('scheduled_task_event_form_relay_state')}
          </InputLabel>
          <Controller
            name={`event.scheduledRelayCommandParams.${relayIndex}.requiredState`}
            control={control}
            render={({
              field,
            }: {
              field: ControllerRenderProps<
                ScheduledEventForm,
                `event.scheduledRelayCommandParams.${number}.requiredState`
              >;
              fieldState: ControllerFieldState;
              formState: UseFormStateReturn<ScheduledEventForm>;
            }): ReactElement => (
              <Select
                {...field}
                labelId={`required-state-label-${relayIndex}`}
                label={t('scheduled_task_event_form_relay_state')}
              >
                <MenuItem value="ON">{t('on')}</MenuItem>
                <MenuItem value="OFF">{t('off')}</MenuItem>
              </Select>
            )}
          />
        </FormControl>
        <FormControl fullWidth size="small" error={!!relayErrors?.hour}>
          <InputLabel id={`hour-label-${relayIndex}`}>
            {t('scheduled_task_event_form_hour')}
          </InputLabel>
          <Controller
            name={`event.scheduledRelayCommandParams.${relayIndex}.hour`}
            control={control}
            render={({
              field,
            }: {
              field: ControllerRenderProps<
                ScheduledEventForm,
                `event.scheduledRelayCommandParams.${number}.hour`
              >;
              fieldState: ControllerFieldState;
              formState: UseFormStateReturn<ScheduledEventForm>;
            }): ReactElement => (
              <Select
                {...field}
                labelId={`hour-label-${relayIndex}`}
                label={t('scheduled_task_event_form_hour')}
              >
                {Array.from({ length: 24 }, (_: unknown, i: number) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ))}
                <MenuItem value={255}>N/A</MenuItem>
              </Select>
            )}
          />
        </FormControl>
        <FormControl fullWidth size="small" error={!!relayErrors?.minutes}>
          <InputLabel id={`minute-label-${relayIndex}`}>
            {t('scheduled_task_event_form_minute')}
          </InputLabel>
          <Controller
            name={`event.scheduledRelayCommandParams.${relayIndex}.minutes`}
            control={control}
            render={({
              field,
            }: {
              field: ControllerRenderProps<
                ScheduledEventForm,
                `event.scheduledRelayCommandParams.${number}.minutes`
              >;
              fieldState: ControllerFieldState;
              formState: UseFormStateReturn<ScheduledEventForm>;
            }): ReactElement => (
              <Select
                {...field}
                labelId={`minute-label-${relayIndex}`}
                label={t('scheduled_task_event_form_minute')}
              >
                {Array.from({ length: 60 }, (_: unknown, i: number) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ))}
                <MenuItem value={255}>N/A</MenuItem>
              </Select>
            )}
          />
        </FormControl>
      </div>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="command configuration tabs">
          <Tab label={`${t('command')} 1`} {...a11yProps(0)} />
          <Tab label={`${t('command')} 2`} {...a11yProps(1)} />
          <Tab label={`${t('command')} 3`} {...a11yProps(2)} />
          <Tab label={`${t('command')} 4`} {...a11yProps(3)} />
        </Tabs>
      </Box>
      <TabPanel value={tabValue} index={0}>
        {renderRelayForm(0)}
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        {renderRelayForm(1)}
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        {renderRelayForm(2)}
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        {renderRelayForm(3)}
      </TabPanel>
    </Box>
  );
};

interface ScheduledRelay4CommandProps {
  form: UseFormReturn<ScheduledEventForm, unknown, undefined>;
}
